import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, Drawer, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import Link from 'next/link';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MenuList } from './menu-list';
import { SidebarDivider } from './sidebar-divider';
import { SidebarFooter } from './sidebar-footer';
import { SidebarItem } from './sidebar-item';

import ConnexLogo from '@/assets/images/ConnexLogo.svg';
import Mouthwave from '@/assets/images/Mouthwave.svg';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { useTelmaPortalStore } from '@/store/telma-portal-store';

const NARROW_BAR_WIDTH = 64;

export const Sidebar: FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const opened = useTelmaPortalStore((state) => state.opened);
    const toggleMenu = useTelmaPortalStore((state) => state.setMenu);
    const { isHgcConnex: isHgcUser } = useUserPermissions();
    const isNarrowBar = !opened && !isMobile;

    const logoSrc = isHgcUser ? ConnexLogo : Mouthwave;
    const logoAlt = isHgcUser ? 'HGC Connex logo' : 'Telma logo';
    const brandName = isHgcUser ? 'HGC Connex' : 'TelmaPortal';
    const sidebarWidth = isNarrowBar
        ? NARROW_BAR_WIDTH
        : // @ts-expect-error custom mixin
          theme.mixins.drawer.width;
    const panelSx = {
        width: sidebarWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
        background: theme.palette.surface.sidebar,
        color: theme.palette.common.white,
        border: 'none',
    };
    const sidebarContent = (
        <Box
            component='nav'
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                px: isNarrowBar ? 1 : 1.5,
                pb: 1.5,
                boxSizing: 'border-box',
            }}
        >
            <Box
                component={Link}
                href='/bots'
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isNarrowBar ? 'center' : 'flex-start',
                    gap: 1.25,
                    minHeight: 64,
                    px: isNarrowBar ? 0 : 1.25,
                    color: 'common.white',
                    textDecoration: 'none',
                }}
            >
                <Image
                    src={logoSrc}
                    alt={logoAlt}
                    priority
                    style={{ height: 26, width: 'auto' }}
                />
                {!isNarrowBar && (
                    <Typography
                        component='span'
                        noWrap
                        sx={{ fontSize: '1.05rem', fontWeight: 600 }}
                    >
                        {brandName}
                    </Typography>
                )}
            </Box>
            <SidebarDivider />

            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <MenuList isNarrowBar={isNarrowBar} />
            </Box>

            <Box>
                <SidebarFooter isNarrowBar={isNarrowBar} />
                <SidebarDivider />
                <SidebarItem
                    icon={
                        isNarrowBar ? (
                            <KeyboardDoubleArrowRight />
                        ) : (
                            <KeyboardDoubleArrowLeft />
                        )
                    }
                    label={isNarrowBar ? t('show_menu') : t('hide_menu')}
                    isNarrowBar={isNarrowBar}
                    expanded={!isNarrowBar}
                    onClick={toggleMenu}
                    data-testid='sidebar-toggle'
                />
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                variant='temporary'
                open={opened}
                onClose={toggleMenu}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: panelSx }}
            >
                {sidebarContent}
            </Drawer>
        );
    }

    return (
        <Box
            component='aside'
            sx={{
                ...panelSx,
                height: '100%',
            }}
        >
            {sidebarContent}
        </Box>
    );
};
